/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Fetches a rendered section, preserving Liquid visibility and price rules.
     */
    async fetchResults(searchParams) {
      const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
      if (!response.ok) throw new Error(response.status);
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const results = template.content.querySelector('#shopify-section-predictive-search');
      if (!results) throw new Error('Missing predictive search section');
      return results;
    }

    /**
     * Adds product matches without discarding other result tabs or suggestions.
     */
    mergeProductResults(results, additionalResults, prioritize = false) {
      const additionalPanel = additionalResults.querySelector('#psearch-products');
      if (!additionalPanel || !additionalPanel.querySelector('.js-search-link[href]')) return;

      const panel = results.querySelector('#psearch-products');
      if (!panel) {
        const tabs = results.querySelector('tabbed-content');
        if (tabs) {
          tabs.querySelector('[role="tablist"]').prepend(additionalResults.querySelector('#psearch-products-tab'));
          tabs.append(additionalPanel);
        } else {
          results.querySelector('.predictive-search__no-results')?.remove();
          results.querySelector('.predictive-search__results').append(additionalResults.querySelector('tabbed-content'));
        }
        const scroller = results.querySelector('.predictive-search__scroller');
        if (scroller.classList.contains('predictive-search__scroller--suggestions-only')) {
          scroller.classList.replace('predictive-search__scroller--suggestions-only', 'predictive-search__scroller--suggestions');
        }
        return;
      }

      const selector = '.predictive-search__item:not(.js-submit)';
      const existing = Array.from(panel.querySelectorAll(selector));
      const additional = Array.from(additionalPanel.querySelectorAll(selector));
      const ordered = prioritize ? [...additional, ...existing] : [...existing, ...additional];
      const seen = new Set();
      const submit = panel.querySelector('.js-submit');
      existing.forEach((item) => item.remove());
      ordered.forEach((item) => {
        const link = item.querySelector('.js-search-link[href]');
        if (!link) return;
        // Search tracking and variant parameters must not duplicate a product.
        const key = new URL(link.getAttribute('href'), window.location.origin).pathname;
        if (seen.has(key) || seen.size >= theme.settings.pSearchLimit) return;
        seen.add(key);
        submit.before(item);
      });
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const requestId = (this.requestId || 0) + 1;
      this.requestId = requestId;
      this.setLiveRegionLoadingState();
      const productType = this.productTypeInput ? this.productTypeInput.value : '';

      let searchFields = 'title,product_type,variants.title,vendor,variants.sku,variants.barcode';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (productType) {
        searchParams = `q=product_type:${encodeURIComponent(productType)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const productParams = (term) => {
          const params = new URLSearchParams(searchParams);
          params.set('q', productType ? `product_type:${productType} AND ${term}` : term);
          params.set('resources[type]', 'product');
          return params;
        };
        const isCode = /^(?=.*\d)[a-z\d._/-]+$/i.test(searchTerm);
        // Explicit fields avoid relying on Shopify to recognize a code as a SKU.
        const skuTerms = isCode ? [`variants.sku:"${searchTerm}"`] : [];
        if (/^\d+$/.test(searchTerm)) skuTerms.push(`variants.sku:"LR-${searchTerm}"`);
        // Explicit tag queries handle the colon as tag text, not a field operator.
        const mfgTerms = isCode
          ? [`tag:"mfg_code:${searchTerm}"`, `tag:"mfg_code: ${searchTerm}"`] : [];
        const fetchCodeResults = (terms, field) => Promise.all(terms.map((term) => {
          const params = productParams(term);
          params.set('resources[options][fields]', field);
          return this.fetchResults(params).catch(() => null);
        }));
        const [resultsEl, mfgResults, skuResults] = await Promise.all([
          this.fetchResults(searchParams),
          fetchCodeResults(mfgTerms, 'tag'),
          fetchCodeResults(skuTerms, 'variants.sku')
        ]);

        // Prioritize code matches even when ordinary results fill the dropdown.
        // Merge SKUs last so they appear ahead of MFG and ordinary matches.
        mfgResults.filter(Boolean).reverse().forEach((result) => {
          this.mergeProductResults(resultsEl, result, true);
        });
        skuResults.filter(Boolean).reverse().forEach((result) => {
          this.mergeProductResults(resultsEl, result, true);
        });

        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        if (this.getQuery() !== searchTerm || this.requestId !== requestId) return;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (this.getQuery() !== searchTerm || this.requestId !== requestId) return;
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
